/**
 * "No Rep Required / Self-Service Event" option for the Lead/Event Rep dropdown.
 *
 * A self-service event has no lead/event rep — the signed partner runs it with
 * the generic QR (Smart Admin "Marketing Video Only"). This is distinct from
 * "rep not yet assigned": both leave sales_position_id null, but self-service is
 * an explicit operator choice persisted on events.is_self_service.
 *
 * Pure mapping helpers so the dropdown<->state translation is unit-tested and
 * the form just applies the result.
 */

/** Sentinel <option> value for the self-service choice (not a real UUID). */
export const SELF_SERVICE_REP_VALUE = "__self_service__";

/** Label shown for the self-service option in the rep dropdown. */
export const SELF_SERVICE_REP_LABEL = "No Rep Required / Self-Service Event";

/**
 * The <select> value for the Lead/Event Rep dropdown given the current rep id
 * and the self-service flag. Self-service wins (shows the sentinel) even if a
 * rep id lingers in state.
 */
export function repSelectValue(
  repId: string | null | undefined,
  isSelfService: boolean,
): string {
  if (isSelfService) {
    return SELF_SERVICE_REP_VALUE;
  }
  return repId ?? "";
}

/**
 * Interpret a Lead/Event Rep <select> change into the rep id + self-service flag
 * the form should store. Selecting the sentinel → no rep + self-service; any
 * other value (a real position id or "") → that rep + not self-service.
 */
export function parseRepSelectChange(value: string): {
  repId: string | null;
  isSelfService: boolean;
} {
  if (value === SELF_SERVICE_REP_VALUE) {
    return { repId: null, isSelfService: true };
  }
  return { repId: value || null, isSelfService: false };
}

/**
 * The sales_position_id to persist for the lead/event rep. Self-service always
 * persists null (no rep), regardless of any lingering rep id.
 */
export function resolveLeadRepPositionId(
  repId: string | null | undefined,
  isSelfService: boolean,
): string | null {
  if (isSelfService) {
    return null;
  }
  return repId ?? null;
}

/**
 * What a save must do about the Lead/Event Rep (MSR activation fix 6, internal re-audit P1-1 / P2-3).
 *
 * The rep is changed ONLY when the operator changed the rep control relative to what the form loaded: a save that
 * did not touch it sends no rep change and writes no sales_position_id (a stale form must not revert a Lead someone
 * else set on the Sales Reps list). Choosing "no rep" or self-service is a change too: the Lead then leaves the Sales
 * Reps list. `retryPending` re-sends the same rep after an earlier sync of it failed (wizard re-entry).
 *
 * leadCall      -> syncLeadRepToSalesReps(event, leadCall.next, leadCall.expected); null = no call
 * selfService   -> the is_self_service value to write in the event update (only after leadCall succeeded, if any);
 *                  undefined = leave it alone
 */
export function planLeadRepSave(
  loaded: {
    repId: string | null | undefined;
    isSelfService: boolean | null | undefined;
  },
  current: { repId: string | null | undefined; isSelfService: boolean },
  retryPending = false,
): {
  leadCall: { next: string | null; expected: string | null } | null;
  selfService: boolean | undefined;
} {
  const loadedRep = resolveLeadRepPositionId(
    loaded.repId,
    !!loaded.isSelfService,
  );
  const loadedSelf = !!loaded.isSelfService;
  const nextRep = resolveLeadRepPositionId(
    current.repId,
    current.isSelfService,
  );
  const repChanged =
    nextRep !== loadedRep || (current.isSelfService && !loadedSelf);
  const leadCall =
    repChanged || (retryPending && nextRep !== null)
      ? { next: nextRep, expected: loadedRep }
      : null;
  return {
    leadCall,
    selfService:
      current.isSelfService !== loadedSelf ? current.isSelfService : undefined,
  };
}

/**
 * The edit form's save of the Lead/Event Rep and the event update, in order (external audit 360cf0f5, P3).
 *
 * 1. the rep change, if any, through `syncLead` (one compare-and-set RPC);
 * 2. the event update through `writeEvent`, carrying is_self_service only when it changed and the rep call (if any)
 *    succeeded. sales_position_id / is_self_service in `updates` are never sent directly.
 *
 * The form's baseline (`loaded.current`) advances PER WRITE, and only after that write succeeded:
 *   - the rep, right after its RPC succeeded (it is committed; a retry must not compare-and-set from the old rep);
 *   - Self-Service, only after the event update that carries it succeeded. If that update throws, the baseline still
 *     says "not Self-Service", so a retry sends is_self_service again (with a no-op compare-and-set of the rep, which
 *     the server answers "no change"). 360cf0f5 advanced Self-Service before the update: a retry then sent nothing
 *     and the event saved with Self-Service off.
 * Rethrows the event update's error (the caller's existing error path). Returns the rep RPC's result, or null.
 */
export async function saveEditedLeadRepAndEvent<R extends { error?: unknown }>(
  loaded: { current: { repId: string | null; isSelfService: boolean } },
  current: { repId: string | null | undefined; isSelfService: boolean },
  updates: Record<string, unknown>,
  syncLead: (next: string | null, expected: string | null) => Promise<R>,
  writeEvent: (updates: Record<string, unknown>) => Promise<unknown>,
): Promise<R | null> {
  const plan = planLeadRepSave(loaded.current, current);
  const out = { ...updates };
  delete out.sales_position_id;
  delete out.is_self_service;
  let leadResult: R | null = null;
  let leadOk = true;
  if (plan.leadCall) {
    leadResult = await syncLead(plan.leadCall.next, plan.leadCall.expected);
    leadOk = !leadResult.error;
    if (leadOk) {
      loaded.current = { ...loaded.current, repId: plan.leadCall.next };
    }
  }
  const selfService = leadOk ? plan.selfService : undefined;
  if (selfService !== undefined) {
    out.is_self_service = selfService;
  }
  await writeEvent(out);
  if (selfService !== undefined) {
    loaded.current = { ...loaded.current, isSelfService: selfService };
  }
  return leadResult;
}
